import React, { useMemo } from 'react';

import classnames from 'classnames';
import omit from 'lodash/omit';

import DateRangePicker from 'components/uiLibrary/DateRangePicker';
import { PrimaryButton, TextButton } from 'components/uiLibrary/LinkButton';
import Skeleton from 'components/uiLibrary/Loaders/Skeleton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import { COMPONENTS, SUB_COMPONENTS } from 'components/Globals/Analytics/constants';

import { DATE_FORMATS, SORT_OPTION_TYPES, TP } from 'constants/index';

import { createDate } from 'utils/date';
import useAppContext from 'utils/hooks/useAppContext';
import { useQuery } from 'utils/react-query';
import { getPredefinedDates } from 'utils/search';

import queries from 'containers/Productions/queries';

import { useTranslation } from 'src/i18n';

import { AGGREGATION_TYPES, FILTER_TYPES, ROUTE_DATE_FORMATS } from 'operabase-router/constants';

import classes from './DatesSelector.module.scss';

const MIN_YEAR = 1996;

const DatesSelector = ({
  handleUpdateCategory,
  categoryDetails,
  onApply,
  onClear,
  categoryId,
  skipFilters = false,
  trackingData,
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { obRouteContext } = useAppContext();
  const { filterParams, linkProps } = obRouteContext || {};
  const { filters } = linkProps || {};

  const { fromDate, toDate } = useMemo(() => {
    if (Object.keys(categoryDetails).length > 0) {
      return categoryDetails;
    }

    if (
      filters?.[FILTER_TYPES.DATE]?.[FILTER_TYPES.DATE_FROM] ||
      filters?.[FILTER_TYPES.DATE]?.[FILTER_TYPES.DATE_TO]
    ) {
      return {
        fromDate: filters?.[FILTER_TYPES.DATE]?.[FILTER_TYPES.DATE_FROM] || null,
        toDate: filters?.[FILTER_TYPES.DATE]?.[FILTER_TYPES.DATE_TO] || null,
      };
    }

    return {};
  }, [categoryDetails, filters]);

  const { data: seasons, isLoading: isSeasonsLoading } = useQuery(
    queries.getFilterOptions({
      filters: skipFilters ? {} : omit(filterParams, [FILTER_TYPES.DATE_FROM, FILTER_TYPES.DATE_TO]),
      aggregationType: AGGREGATION_TYPES[FILTER_TYPES.SEASON],
      limit: 300,
      sort: SORT_OPTION_TYPES.YEAR_DESC.value,
    }),
  );

  const [minYear, minDate] = useMemo(() => {
    if (seasons?.data?.length > 0) {
      const year = Math.min(...seasons.data.map(season => Number(season.id)));
      return [year, new Date(`${year}-01-01`)];
    }

    return [MIN_YEAR, new Date(`${MIN_YEAR}-01-01`)];
  }, [seasons?.data]);
  const DATES = useMemo(() => {
    if (isSeasonsLoading || !seasons?.total) {
      return [];
    }

    const predefinedDates = getPredefinedDates();
    const currentYear = createDate().format(DATE_FORMATS.YEAR);
    const { dates: seasonsDates = [], hasCurrentYear = false } = seasons.data.reduce(
      (acc, season) => {
        // NOTE: We don't want to show seasons before 1996
        if (Number(season?.id) < minYear) {
          return acc;
        }

        const yearDate = createDate(`${season.id}`, ROUTE_DATE_FORMATS.YEAR);

        if (season.id === currentYear) {
          acc.hasCurrentYear = true;
        }

        acc.dates.push({
          label: season.name,
          fromDate: yearDate.format(ROUTE_DATE_FORMATS.STANDARD),
          toDate: yearDate.endOf('year').format(ROUTE_DATE_FORMATS.STANDARD),
        });
        return acc;
      },
      { dates: [], hasCurrentYear: false },
    );

    if (hasCurrentYear) {
      return [...predefinedDates, ...seasonsDates];
    }

    return seasonsDates;
  }, [isSeasonsLoading, minYear, seasons?.data, seasons?.total]);

  const isValidDate = useMemo(
    () =>
      (fromDate || toDate) &&
      (!fromDate || !toDate || fromDate <= toDate) &&
      (!fromDate || new Date(fromDate) >= minDate) &&
      (!toDate || new Date(toDate) >= minDate),
    [fromDate, minDate, toDate],
  );

  const handleDateChange = (date, dateType) => {
    const updatedDate = dateType
      ? {
          [`${dateType}Date`]: date,
          label: '',
        }
      : date;
    handleUpdateCategory({
      ...categoryDetails,
      ...updatedDate,
    });
  };

  const selectedDates = useMemo(() => {
    if (fromDate && toDate) {
      return { fromDate, toDate };
    }

    return {};
  }, [fromDate, toDate]);

  return (
    <div className={classes.datesSelector}>
      <div className={classes.content}>
        <DateRangePicker date={selectedDates} onChange={handleDateChange} minDate={minDate} />
        {isSeasonsLoading ? (
          <Skeleton shape="rectangular" height={50} className={classes.skeleton} />
        ) : (
          DATES.map(date => (
            <TextButton
              key={date.label}
              onClick={() => handleDateChange(date)}
              styles={{
                root: classnames(classes.card, {
                  [classes.card__selected]: fromDate === date.fromDate && toDate === date.toDate,
                }),
              }}
              trackingData={{
                ...trackingData,
                component: COMPONENTS.DATES,
                subComponent: SUB_COMPONENTS.DATE,
                meta: {
                  id: date?.label,
                },
              }}
            >
              <Typography size="12">{t(date.label)}</Typography>
            </TextButton>
          ))
        )}
      </div>
      <div className={classes.actions}>
        <TextButton
          disableUnderline
          leftIcon={<SpriteIcon icon="replay-bold" size="14" />}
          onClick={() => onClear(categoryId)}
          styles={{ root: classes.clear }}
          trackingData={{
            ...trackingData,
            component: COMPONENTS.DATES,
            subComponent: SUB_COMPONENTS.CLEAR_CTA,
          }}
        >
          <Typography color="secondary" size="12" className={classes.clear__label} disabled={!isValidDate}>
            {t(`${TP}.FORM_CLEAR_ALL`)}
          </Typography>
        </TextButton>
        <PrimaryButton
          styles={{ root: classes.submit }}
          onClick={onApply}
          disabled={!isValidDate}
          trackingData={{
            ...trackingData,
            component: COMPONENTS.DATES,
            subComponent: SUB_COMPONENTS.SUBMIT_CTA,
          }}
        >
          {t(`${TP}.m_APPLY`)}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default DatesSelector;
